import { Box, CircularProgress } from '@mui/material';
import { getApplicationLinkValidityRequest } from 'api/requests/public-application-requests';
import i18next from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IApplicationLinkValidity } from 'types/employee/IJobApplicantApplication';
import Lang from 'types/Lang';
import ApplicationForm from './ApplicationForm';
import LanguageSwitcher from './LanguageSwitcher';
import LinkNoLongerActive from './LinkNoLongerActive';
import { ContentColumn, PageShell } from './styles';

type LinkState = 'checking' | 'valid' | 'inactive';

const PublicApplicationPage = (): JSX.Element => {
  const { token } = useParams<{ token: string }>();
  const [linkState, setLinkState] = useState<LinkState>('checking');
  const [context, setContext] = useState<IApplicationLinkValidity | null>(null);

  const checkLink = useCallback(async () => {
    if (!token) {
      setLinkState('inactive');
      return;
    }
    try {
      const validity = await getApplicationLinkValidityRequest(token);
      setContext(validity);
      // The endpoint always answers 200: `isValid` is the signal, and it is false for an unknown
      // token, a revoked link and a disabled feature alike.
      setLinkState(validity.isValid ? 'valid' : 'inactive');
    } catch {
      // Showing a form we cannot submit would be worse than showing nothing.
      setLinkState('inactive');
    }
  }, [token]);

  useEffect(() => {
    checkLink();
  }, [checkLink]);

  // Applicants arrive cold from a shared link, so the browser locale is the only signal. An
  // unsupported one leaves the app default in place; the switcher overrides either way.
  useEffect(() => {
    const browserLanguage = navigator.language.slice(0, 2).toLowerCase();
    if ((Object.values(Lang) as string[]).includes(browserLanguage)) {
      i18next.changeLanguage(browserLanguage);
    }
  }, []);

  return (
    <PageShell>
      <ContentColumn>
        <LanguageSwitcher />
        {linkState === 'checking' && (
          <Box sx={{ display: 'flex', flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={48} sx={{ color: 'primary.main' }} />
          </Box>
        )}
        {linkState === 'inactive' && <LinkNoLongerActive />}
        {linkState === 'valid' && (
          <ApplicationForm
            token={token as string}
            companyName={context?.companyName ?? null}
            onLinkRevoked={() => setLinkState('inactive')}
          />
        )}
      </ContentColumn>
    </PageShell>
  );
};

export default PublicApplicationPage;
